import * as ImagePicker from 'expo-image-picker';
import React, { useState } from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { toast } from 'react-toastify';

import { images } from '../../assets/images';
import { fonts, scale } from '../../Styles';
import { ProfileScreenPropsV2, pronouns } from '../containers/ProfileContainerV2';
import { MNOHSAvatar } from '../MNOHSAvatar';
import { MNOHSButton } from '../MNOHSButton';
import { MNOHSDashboardWrapper } from '../MNOHSDashboardWrapper';
import { MNOHSFloatingIndicator } from '../MNOHSFloatingIndicator';
import { MNOHSPicker } from '../MNOHSPicker.web';
import { MNOHSTextInput } from '../MNOHSTextInput';

export const ProfileScreenV2: React.FC<ProfileScreenPropsV2> = ({
  student,
  error,
  buttonState,
  isEditing,
  isFetchingStudent,
  userUpdatedIsEditing,
  userPressedSaveProfile,
}) => {
  const initialPronoun = pronouns.includes(student?.pronoun || '') ? student?.pronoun ?? '' : 'custom';
  const initialCustomPronoun = initialPronoun === 'custom' ? student?.pronoun ?? '' : '';

  // Why these states are here:
  // We want to lessen container re-renders
  const [photoUrl, setPhotoUrl] = useState(student?.imageBase64 || '');
  const [preferredPronoun, setPreferredPronoun] = useState(initialPronoun);
  const [customPronoun, setCustomPronoun] = useState(initialCustomPronoun);

  // Listens to changes in student object and update the states
  React.useEffect(() => {
    setPhotoUrl(student?.imageBase64 || '');
    setPreferredPronoun(initialPronoun);
    setCustomPronoun(initialCustomPronoun);
  }, [student?.imageBase64, student?.pronoun]);

  return (
    <MNOHSDashboardWrapper headerTitle={'Profile'} padded={true}>
      {Boolean(isFetchingStudent && !student) && <MNOHSFloatingIndicator />}
      {isEditing ? (
        <TouchableOpacity
          activeOpacity={0.75}
          style={styles.editButton}
          onPress={() => userUpdatedIsEditing(false)}
          accessibilityLabel="Edit Profile">
          <Image source={images.xIcon} style={styles.xIcon} />
        </TouchableOpacity>
      ) : (
        <TouchableOpacity
          activeOpacity={0.75}
          style={styles.editButton}
          onPress={() => userUpdatedIsEditing(true)}
          accessibilityLabel="View profile without saving">
          <Image source={images.pencilIcon} style={styles.pencilIcon} resizeMode="contain" />
        </TouchableOpacity>
      )}

      <MNOHSAvatar
        style={styles.avatar}
        avatarURL={photoUrl}
        disabled={!isEditing}
        accessibilityLabel={isEditing ? 'Edit profile photo' : 'Profile photo'}
        onPress={async () => {
          const permissionResult = await ImagePicker.requestCameraRollPermissionsAsync();
          if (!permissionResult.granted) {
            toast.error('Permission to access camera roll is required!');
            return;
          }

          const pickerResult = await ImagePicker.launchImageLibraryAsync({
            mediaTypes: ImagePicker.MediaTypeOptions.All,
            base64: true,
            allowsEditing: true,
            aspect: [1, 1],
            quality: 0.25,
          });

          if (!pickerResult.cancelled) {
            setPhotoUrl(pickerResult.uri);
          }
        }}
      />

      <Text style={styles.headerText}>
        {student?.firstName} {student?.lastName} {student?.pronoun ? `(${student?.pronoun})` : ''}
      </Text>
      <Text style={styles.email}>{student?.email}</Text>

      {isEditing && (
        <View style={styles.editor}>
          <MNOHSPicker
            title="PERSONAL PRONOUN"
            options={pronouns}
            selectedValue={preferredPronoun}
            onValueChange={setPreferredPronoun}
            style={styles.input}
            accessibilityLabel="Preferred pronoun picker"
          />
          {preferredPronoun === 'custom' && (
            <MNOHSTextInput
              title="ENTER CUSTOM PRONOUN"
              defaultValue={customPronoun}
              onBlur={e => setCustomPronoun(e.nativeEvent.text)}
              containerStyle={_styles.input}
              accessibilityLabel="Custom pronoun text input"
              floating
            />
          )}
          {Boolean(error) && <Text style={styles.headerText}>{error}</Text>}
          <MNOHSButton
            title="SAVE PROFILE"
            onPress={() => userPressedSaveProfile({ preferredPronoun, customPronoun, photoUrl })}
            state={buttonState}
            style={_styles.saveButton}
            accessibilityLabel="Save profile"
          />
        </View>
      )}
    </MNOHSDashboardWrapper>
  );
};

// styles for the web
// if you wrap styling for MNOHSTextInput inside StyleSheet.create
// it doesn't work for some reason
const _styles = {
  input: {
    width: '100%',
    marginBottom: '8%',
  },
  saveButton: {
    marginTop: scale(16),
    marginBottom: scale(24),
  },
};

const styles = StyleSheet.create({
  editButton: {
    alignSelf: 'flex-end',
    margin: '5%',
    marginBottom: 0,
    maxHeight: '10%',
  },
  pencilIcon: {
    aspectRatio: 1,
    width: scale(20),
    height: scale(20),
  },
  xIcon: {
    aspectRatio: 1,
    width: scale(20),
    height: scale(20),
  },
  avatar: {
    marginVertical: '5%',
  },
  headerText: {
    ...fonts.header2,
    textAlign: 'center',
    marginVertical: '5%',
  },
  userInfo: {
    ...fonts.paragraph,
    textAlign: 'center',
    marginVertical: '1%',
  },
  email: {
    ...fonts.paragraph,
    marginTop: '1%',
    marginBottom: '10%',
    textAlign: 'center',
  },
  editor: {
    width: '100%',
    alignItems: 'center',
    alignSelf: 'center',
  },
  input: {
    width: '100%',
    marginBottom: '8%',
  },
});
